import { App, Skeleton } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { channelApi, channelQueryKeys, type ChannelCard } from '@entities/channel';
import {
  usePatchWidgetConfigMutation,
  useWidgetConfig,
  useWidgetSnippet,
  widgetApi,
  widgetQueryKeys,
  type WidgetConfig,
  type WidgetConfigPatch,
} from '@entities/widget-config';
import { extractErrorMessage } from '@shared/api';
import { useUnsavedGuard } from '@shared/ui/v2';
import { hostsOf, originsFor, siteHost } from './sites';

// У каждого сайта два адреса (с www и без), бэкенд принимает до 20.
const MAX_SITES = 10;
import { WidgetPreview } from './WidgetPreview';
import { radioKeys } from './chUi';
import './widget.css';

const PRESETS = ['#1F2937', '#2563EB', '#16A34A', '#B45309', '#DC2626', '#7C3AED'];
const COLOR_NAMES: Record<string, string> = {
  '#1F2937': 'Графит',
  '#2563EB': 'Синий',
  '#16A34A': 'Зелёный',
  '#B45309': 'Янтарный',
  '#DC2626': 'Красный',
  '#7C3AED': 'Фиолетовый',
};
const HEX = /^#[0-9A-Fa-f]{6}$/;

export interface WForm {
  display_name: string;
  greeting: string;
  brand_color: string;
  position: 'right' | 'left';
  theme: 'auto' | 'light' | 'dark';
  proactive_enabled: boolean;
  proactive_delay_sec: number;
  proactive_show_avatar: boolean;
  sites: string[];
  reset_bg: boolean;
}

function fromConfig(c: WidgetConfig): WForm {
  return {
    display_name: c.display_name,
    greeting: c.greeting,
    brand_color: c.brand_color.toUpperCase(),
    position: c.position,
    theme: c.theme,
    proactive_enabled: c.proactive_enabled,
    proactive_delay_sec: c.proactive_delay_sec,
    proactive_show_avatar: c.proactive_show_avatar,
    sites: hostsOf(c.allowed_origins),
    reset_bg: false,
  };
}

/** Сайты → origins: у прежних доменов сохраняем их origins как были, новые — с www и без. */
function originsOf(sites: string[], current: string[]) {
  const out: string[] = [];
  for (const host of sites) {
    const kept = current.filter((o) => siteHost(o) === host);
    for (const o of kept.length ? kept : (originsFor(host) ?? []))
      if (!out.includes(o)) out.push(o);
  }
  return out;
}

function diff(f: WForm, c: WidgetConfig): WidgetConfigPatch {
  const p: WidgetConfigPatch = {};
  const base = fromConfig(c);
  if (f.display_name.trim() !== base.display_name) p.display_name = f.display_name.trim();
  if (f.greeting.trim() !== base.greeting) p.greeting = f.greeting.trim();
  if (f.brand_color !== base.brand_color) p.brand_color = f.brand_color;
  if (f.position !== base.position) p.position = f.position;
  if (f.theme !== base.theme) p.theme = f.theme;
  if (f.proactive_enabled !== base.proactive_enabled) p.proactive_enabled = f.proactive_enabled;
  if (f.proactive_delay_sec !== base.proactive_delay_sec)
    p.proactive_delay_sec = f.proactive_delay_sec;
  if (f.proactive_show_avatar !== base.proactive_show_avatar)
    p.proactive_show_avatar = f.proactive_show_avatar;
  if (f.sites.join('|') !== base.sites.join('|'))
    p.allowed_origins = originsOf(f.sites, c.allowed_origins);
  if (f.reset_bg) {
    p.reset_bg_custom = true;
    p.reset_bot_bg_custom = true;
  }
  return p;
}

function devInstruction(snippet: string, sites: string[]) {
  return [
    'Нужно поставить на сайт окно чата с ассистентом RiChat.',
    '',
    'Вставьте этот код на все страницы сайта перед закрывающим тегом </body>:',
    '',
    snippet,
    '',
    sites.length
      ? `Окно откроется только на этих сайтах: ${sites.join(', ')}. Другие адреса владелец добавляет в кабинете RiChat.`
      : 'Окно откроется только на сайтах, которые владелец добавил в кабинете RiChat.',
    'Больше ничего настраивать не нужно: внешний вид меняется в кабинете, без правки кода.',
  ].join('\n');
}

/** Экран 7 «Окно чата на сайте» (настройка виджета), утверждён 28.10.2026. */
export function WidgetPage({ assistantId, c }: { assistantId: string; c: ChannelCard }) {
  const { message, modal } = App.useApp();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data: cfg, error } = useWidgetConfig(c.id);
  const { data: snip } = useWidgetSnippet(c.id);
  const patch = usePatchWidgetConfigMutation(c.id);
  const [form, setForm] = useState<WForm | null>(null);
  const [siteInput, setSiteInput] = useState('');
  const [siteErr, setSiteErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const [headSlot, setHeadSlot] = useState<HTMLElement | null>(null);
  useEffect(() => setHeadSlot(document.getElementById('ch-head-actions')), []);

  useEffect(() => {
    if (cfg && !form) setForm(fromConfig(cfg));
  }, [cfg, form]);

  const changes = useMemo(() => (form && cfg ? diff(form, cfg) : {}), [form, cfg]);
  const dirty = Object.keys(changes).length > 0;
  useUnsavedGuard(dirty, 'Изменения в окне чата не сохранены.');

  if (error)
    return (
      <div className="v2-card v2-card-pad">
        Не удалось загрузить окно чата: {extractErrorMessage(error)}
      </div>
    );
  if (!cfg || !form) return <Skeleton active paragraph={{ rows: 8 }} />;

  const set = <K extends keyof WForm>(k: K, v: WForm[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f));
  const colorOk = HEX.test(form.brand_color);
  const nameOk = form.display_name.trim().length > 0;

  const save = async () => {
    if (!dirty) return;
    if (!nameOk) {
      message.error('Впишите имя ассистента — его видно в шапке окна');
      return;
    }
    if (!colorOk) {
      message.error('Цвет — в виде #RRGGBB, например #1F2937');
      return;
    }
    if (!form.greeting.trim()) {
      message.error('Впишите приветствие — его клиент видит первым');
      return;
    }
    if (!form.sites.length) {
      message.error('Добавьте хотя бы один сайт — окно чата работает только на них');
      return;
    }
    if (form.sites.length > MAX_SITES) {
      message.error(`Не больше ${MAX_SITES} сайтов`);
      return;
    }
    try {
      const next = await patch.mutateAsync(changes);
      setForm(fromConfig(next));
      qc.invalidateQueries({ queryKey: channelQueryKeys.all });
      message.success('Сохранено — на сайте изменится при следующем открытии страницы');
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const addSite = () => {
    const host = siteHost(siteInput);
    if (!originsFor(siteInput)) {
      setSiteErr('Не похоже на адрес сайта. Пример: kofeservice.ru');
      return;
    }
    if (!form.sites.includes(host) && form.sites.length >= MAX_SITES) {
      setSiteErr(`Не больше ${MAX_SITES} сайтов — уберите лишний`);
      return;
    }
    if (!form.sites.includes(host)) set('sites', [...form.sites, host]);
    setSiteInput('');
    setSiteErr(null);
  };

  const copy = async (text: string, ok: string) => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(ok);
    } catch {
      message.info('Не удалось скопировать — выделите текст и скопируйте вручную');
    }
  };

  const upload = async (file: File) => {
    setUploading(true);
    try {
      const next = await widgetApi.uploadAvatar(c.id, file);
      qc.setQueryData(widgetQueryKeys.config(c.id), next);
      message.success('Фото сохранено');
    } catch (e) {
      message.error(extractErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async () => {
    try {
      const next = await widgetApi.patchConfig(c.id, { avatar_url: '' });
      qc.setQueryData(widgetQueryKeys.config(c.id), next);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const disconnect = () =>
    modal.confirm({
      title: 'Отключить окно чата?',
      content:
        'Окно пропадёт со всех сайтов, где вставлен код, а переписка из него удалится из «Диалогов».',
      okText: 'Отключить',
      okButtonProps: { danger: true },
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await channelApi.delete(c.id);
          qc.invalidateQueries({ queryKey: channelQueryKeys.all });
          qc.invalidateQueries({ queryKey: ['cabinet'] });
          setForm(fromConfig(cfg)); // снять «несохранённые изменения» перед уходом
          navigate(`/assistants/${assistantId}/channels`, { replace: true });
        } catch (e) {
          message.error(extractErrorMessage(e));
        }
      },
    });

  const saveBar = (
    <div className="wg-savebar">
      <span className="v2-xs v2-muted wg-save-long">
        {dirty ? 'Есть несохранённые изменения' : 'Изменения появятся на сайте после сохранения'}
      </span>
      {dirty && <span className="v2-xs wg-save-short">Не сохранено</span>}
      <button
        type="button"
        className="v2-btn is-primary"
        disabled={!dirty || patch.isPending}
        onClick={save}
      >
        {patch.isPending ? 'Сохраняем…' : 'Сохранить'}
      </button>
    </div>
  );

  const custom = !PRESETS.includes(form.brand_color);
  const letter = (form.display_name.trim()[0] || 'A').toUpperCase();
  const snippet = snip?.snippet ?? '';

  return (
    <div className="wg-grid">
      <div className="wg-main">
        {headSlot ? createPortal(saveBar, headSlot) : saveBar}

        <section className="v2-card wg-card" aria-labelledby="h-install">
          <h2 id="h-install" className="v2-h2">
            Установка на сайт
          </h2>
          <div className="v2-small wg-text">
            Вставьте код на все страницы сайта перед закрывающим тегом &lt;/body&gt; — или отправьте
            его разработчику сайта.
          </div>
          <pre className="wg-code" aria-label="Код окна чата">
            {snippet || '…'}
          </pre>
          <div className="wg-row-wrap">
            <button
              type="button"
              className="v2-btn"
              disabled={!snippet}
              onClick={() => copy(snippet, 'Код скопирован')}
            >
              Скопировать код
            </button>
            <button
              type="button"
              className="v2-btn"
              disabled={!snippet}
              onClick={() =>
                copy(devInstruction(snippet, form.sites), 'Инструкция для разработчика скопирована')
              }
            >
              Скопировать инструкцию для разработчика
            </button>
          </div>
          <div className="wg-sep" />
          <div className="wg-label" id="l-sites">
            Сайты, где работает окно
          </div>
          <div className="wg-chips" role="list" aria-labelledby="l-sites">
            {form.sites.map((h) => (
              <span key={h} className="wg-chip" role="listitem">
                {h}
                <button
                  type="button"
                  aria-label={`Убрать ${h}`}
                  onClick={() =>
                    set(
                      'sites',
                      form.sites.filter((x) => x !== h),
                    )
                  }
                >
                  ✕
                </button>
              </span>
            ))}
            <input
              className="wg-chip-add"
              value={siteInput}
              placeholder="+ добавить сайт"
              aria-label="Добавить сайт"
              onChange={(e) => {
                setSiteInput(e.target.value);
                setSiteErr(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ',') {
                  e.preventDefault();
                  addSite();
                }
              }}
              onBlur={() => siteInput.trim() && addSite()}
            />
          </div>
          {siteErr && (
            <div role="alert" className="ch-err">
              {siteErr}
            </div>
          )}
          {!form.sites.length && (
            <div role="alert" className="wg-warn">
              Добавьте адрес сайта — без него окно чата нигде не откроется.
            </div>
          )}
          <div className="v2-xs v2-muted">
            Если кто-то скопирует код к себе, на чужом сайте окно не откроется.
          </div>
        </section>

        <section className="v2-card wg-card" aria-labelledby="h-look">
          <h2 id="h-look" className="v2-h2">
            Внешний вид
          </h2>
          <div className="wg-two">
            <div className="wg-field">
              <label className="wg-label" htmlFor="w-name">
                Имя ассистента
              </label>
              <input
                id="w-name"
                className="st-input"
                maxLength={40}
                value={form.display_name}
                onChange={(e) => set('display_name', e.target.value)}
                aria-invalid={!nameOk}
              />
              <div className="v2-xs v2-muted">Видно в шапке окна и в приглашении</div>
            </div>
            <div className="wg-field">
              <span className="wg-label">Фото ассистента</span>
              <div className="wg-photo">
                {cfg.avatar_url ? (
                  <img src={cfg.avatar_url} alt="" className="wg-ava" />
                ) : (
                  <span className="wg-ava is-letter">{letter}</span>
                )}
                <button
                  type="button"
                  className="v2-btn"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                >
                  {uploading ? 'Загружаем…' : cfg.avatar_url ? 'Заменить' : 'Загрузить'}
                </button>
                {cfg.avatar_url && (
                  <button type="button" className="v2-btn" onClick={removePhoto}>
                    Убрать
                  </button>
                )}
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  hidden
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    if (f) upload(f);
                  }}
                />
              </div>
              <div className="v2-xs v2-muted">
                Не загрузите — возьмём значок вашего сайта. Фото сохраняется сразу.
              </div>
            </div>
          </div>
          <div className="wg-field">
            <label className="wg-label" htmlFor="w-greet">
              Приветствие
            </label>
            <input
              id="w-greet"
              className="st-input"
              maxLength={120}
              value={form.greeting}
              onChange={(e) => set('greeting', e.target.value)}
            />
            <div className="v2-xs v2-muted">Первое сообщение в пустом чате и текст приглашения</div>
          </div>
          <div className="wg-field">
            <span className="wg-label" id="l-color">
              Цвет
            </span>
            <div
              className="wg-colors"
              role="radiogroup"
              aria-labelledby="l-color"
              onKeyDown={radioKeys}
            >
              {PRESETS.map((col) => (
                <button
                  key={col}
                  type="button"
                  role="radio"
                  aria-checked={form.brand_color === col}
                  aria-label={COLOR_NAMES[col] ?? col}
                  title={COLOR_NAMES[col]}
                  tabIndex={form.brand_color === col || (custom && col === PRESETS[0]) ? 0 : -1}
                  className={form.brand_color === col ? 'wg-swatch is-on' : 'wg-swatch'}
                  style={{ background: col }}
                  onClick={() => set('brand_color', col)}
                />
              ))}
              <span className="wg-vsep" />
              <span className="v2-small v2-muted">Свой</span>
              <label className={custom ? 'wg-swatch is-on wg-picker' : 'wg-swatch wg-picker'}>
                <span className="v2-sr">Выбрать свой цвет</span>
                <input
                  type="color"
                  value={colorOk ? form.brand_color : '#1F2937'}
                  onChange={(e) => set('brand_color', e.target.value.toUpperCase())}
                />
                <span style={{ background: colorOk ? form.brand_color : '#1F2937' }} />
              </label>
              <input
                className={colorOk ? 'st-input wg-hex' : 'st-input wg-hex is-err'}
                value={form.brand_color}
                maxLength={7}
                aria-label="Код цвета"
                onChange={(e) => {
                  let v = e.target.value.trim().toUpperCase();
                  if (v && !v.startsWith('#')) v = `#${v}`;
                  set('brand_color', v);
                }}
              />
            </div>
            <div className="v2-xs v2-muted">
              Кнопка чата, ваши сообщения и кнопка отправки. Цвет текста подберём сами.
            </div>
          </div>
          <div className="wg-two">
            <div className="wg-field">
              <span className="wg-label" id="l-pos">
                Где на странице
              </span>
              <div
                className="wg-seg"
                role="radiogroup"
                aria-labelledby="l-pos"
                onKeyDown={radioKeys}
              >
                {(
                  [
                    ['right', 'Справа снизу'],
                    ['left', 'Слева снизу'],
                  ] as const
                ).map(([v, t]) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={form.position === v}
                    tabIndex={form.position === v ? 0 : -1}
                    className={form.position === v ? 'is-on' : undefined}
                    onClick={() => set('position', v)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <div className="wg-field">
              <span className="wg-label" id="l-theme">
                Тема
              </span>
              <div
                className="wg-seg"
                role="radiogroup"
                aria-labelledby="l-theme"
                onKeyDown={radioKeys}
              >
                {(
                  [
                    ['auto', 'Как на устройстве'],
                    ['light', 'Светлая'],
                    ['dark', 'Тёмная'],
                  ] as const
                ).map(([v, t]) => (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={form.theme === v}
                    tabIndex={form.theme === v ? 0 : -1}
                    className={form.theme === v ? 'is-on' : undefined}
                    onClick={() => set('theme', v)}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {(cfg.bg_custom || cfg.bot_bg_custom) && !form.reset_bg && (
            <div className="wg-warn" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <span style={{ flex: 1 }}>
                У окна остался свой фон из прежних настроек — тема на него не влияет.
              </span>
              <button type="button" className="v2-btn" onClick={() => set('reset_bg', true)}>
                Вернуть фон темы
              </button>
            </div>
          )}
        </section>

        <section className="v2-card wg-card" aria-labelledby="h-invite">
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ flex: 1 }}>
              <h2 id="h-invite" className="v2-h2">
                Приглашение к диалогу
              </h2>
              <div className="v2-small wg-text" style={{ marginTop: 4 }}>
                Через несколько секунд рядом с кнопкой появится сообщение с приветствием — так
                посетители чаще задают вопрос.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.proactive_enabled}
              aria-labelledby="h-invite"
              className={form.proactive_enabled ? 'st-switch is-on' : 'st-switch'}
              onClick={() => set('proactive_enabled', !form.proactive_enabled)}
            >
              <span />
            </button>
          </div>
          {form.proactive_enabled && (
            <div className="wg-row-wrap" style={{ alignItems: 'center' }}>
              <label htmlFor="w-delay" className="v2-small">
                Показать через
              </label>
              <input
                id="w-delay"
                type="number"
                min={0}
                max={60}
                className="st-input"
                style={{ width: 80 }}
                value={form.proactive_delay_sec}
                onChange={(e) =>
                  set(
                    'proactive_delay_sec',
                    Math.max(0, Math.min(60, Math.round(Number(e.target.value) || 0))),
                  )
                }
              />
              <span className="v2-small">секунд</span>
              <label className="wg-check">
                <input
                  type="checkbox"
                  checked={form.proactive_show_avatar}
                  onChange={(e) => set('proactive_show_avatar', e.target.checked)}
                />
                С фото ассистента
              </label>
            </div>
          )}
        </section>

        <section className="v2-card wg-card ch-danger" aria-labelledby="h-off">
          <div
            className="wg-row-wrap"
            style={{ alignItems: 'center', justifyContent: 'space-between' }}
          >
            <div style={{ flex: '1 1 320px' }}>
              <h2 id="h-off" className="v2-h2">
                Отключить окно чата
              </h2>
              <div className="v2-small wg-text" style={{ marginTop: 4 }}>
                {/* ⚠️ Макет: «Переписка сохранится в «Диалогах»» — сейчас удаление канала
                    удаляет и диалоги (каскад в БД); пишем честно, решение за Игорем. */}
                Окно пропадёт со всех сайтов, где вставлен код. Переписка из него удалится из
                «Диалогов».
              </div>
            </div>
            <button type="button" className="v2-btn ch-btn-danger" onClick={disconnect}>
              Отключить
            </button>
          </div>
        </section>
      </div>

      <aside className="wg-side" aria-label="Как это увидят посетители">
        <WidgetPreview form={form} avatarUrl={cfg.avatar_url} site={form.sites[0]} />
      </aside>
    </div>
  );
}
